import { IconContentRemoveFormat } from '@doltech/icons/IconContentRemoveFormat';
import cl from 'classnames';
import { LayoutGroup, motion } from 'framer-motion';
import * as React from 'react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../colors-v2';
import { Separator } from '../../../../Layout/Landing/Content/Separator';
import { ColorDefinition } from '../types';

export interface ColorSelectorProps extends React.HTMLAttributes<HTMLDivElement> {
  colors: ColorDefinition[];
  currentColor?: string;
  onSelectColor: (color: ColorDefinition) => void;
  onDeselectColor?: () => void;
  showDeselectColor?: boolean;
  useBlackBorder?: boolean;
}

const ColorSelectionPanelMain = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;

  .icon-remove-wrapper {
    display: grid;
    place-items: center;
    font-size: 24px;
    line-height: 0;
    transition: all 0.2s ease;
    cursor: pointer;
    &:hover {
      transform: scale(1.02);
    }
    &:active {
      transform: scale(0.96);
    }
  }
`;

const CircleFill = styled.div<{ color: string; isActive?: boolean }>`
  min-width: 30px;
  min-height: 30px;
  width: 30px;
  height: 30px;
  position: relative;
  display: grid;
  cursor: pointer;
  place-items: center;
  transition: all 0.2s ease;
  &:active {
    transform: scale(0.98);
  }
  .circle-fill-inner {
    background: ${(p) => p.color};
    border-radius: 50%;
    min-width: 24px;
    min-height: 24px;
    width: 24px;
    height: 24px;
  }

  .outline {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border: 1.5px solid transparent;
    border-radius: 50%;
  }
`;


const spring = {
  type: 'spring',
  stiffness: 300,
  damping: 40,
};

const ColorSelector = ({
  colors,
  onSelectColor,
  onDeselectColor,
  currentColor,
  showDeselectColor = false,
  useBlackBorder = false,
  className,
  ...rest
}: ColorSelectorProps) => {
  return (
    <LayoutGroup id="outline">
      <ColorSelectionPanelMain className={cl('color-selection-panel', className)} {...rest}>
        {colors.map((item) => {
          const { color, key } = item;
          return (
            <CircleFill
              onMouseDown={(e) => {
                e.preventDefault();
                onSelectColor(item);
              }}
              key={key}
              color={color}
            >
              {currentColor === key && (
                <motion.div
                  initial={false}
                  layoutId="outline"
                  className="outline"
                  animate={{ borderColor: useBlackBorder ? '#000' : color }}
                  transition={spring}
                />
              )}
              <div className="circle-fill-inner" />
            </CircleFill>
          );
        })}
        {showDeselectColor && (
          <>
            <Separator variant="vertical" />
            <div
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                onDeselectColor();
              }}
              className="icon-remove-wrapper"
            >
              <IconContentRemoveFormat />
            </div>
          </>
        )}
      </ColorSelectionPanelMain>
    </LayoutGroup>
  );
};

export default React.memo(ColorSelector);

export const ColorSelectionLightStyled = styled.div`
  background: ${colorsV2.white100};
  border: 1px solid ${colorsV2.black10};

  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  padding: 12px 16px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 8px;
  align-items: center;

  svg {
    font-size: 24px;
    line-height: 0;
    color: ${colorsV2.black100};
  }

  > div {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: auto;
    gap: 8px;
  }
`;

export const ColorSelectionLightInlineStyled = styled.div`
  background: ${colorsV2.white100};
  border-radius: 12px;
  padding: 12px 16px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 8px;
  align-items: center;

  svg {
    font-size: 24px;
    line-height: 0;
    color: ${colorsV2.black100};
  }

  > div {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: auto;
    gap: 8px;
  }
`;
